import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { TableCell } from '@anytag/shared-atoms';
import { usePostDetailsParam } from '@anytag/shared-hooks';
import { InstagramReelPostItem, InstagramReelPostSortField } from '@anytag/graphql';
import { formatDurationToDaysHoursMinutesSeconds, formatIntNumber, formatPercent } from '@anytag/shared-utils';

export const useAnalyticsInstagramReelPostsListTable = () => {
  const { t } = useTranslation();
  const { setPostDetailsParam } = usePostDetailsParam();

  const columns: ColumnDef<InstagramReelPostItem>[] = [
    {
      accessorKey: 'post',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Post'),
      cell: ({ row }) => (
        <TableCell.Post
          title={row.original.content || ''}
          thumbnail={row.original.thumbnail || ''}
          onClick={() => setPostDetailsParam(row.original.id)}
        />
      )
    },
    {
      accessorKey: 'views',
      header: t<string>('HeaderColumn.View'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.ViewHelp'),
        sortField: InstagramReelPostSortField.VIEWS
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.views)} />
    },
    {
      accessorKey: 'viewsByFollowersRate',
      header: t<string>('HeaderColumn.Views by Followers'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.viewsByFollowersHelp'),
        sortField: InstagramReelPostSortField.VIEWS_BY_FOLLOWERS_RATE
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.viewsByFollowersRate, false)} unit="%" />
    },
    {
      accessorKey: 'viewsByNonFollowersRate',
      header: t<string>('HeaderColumn.Views by Non-Followers'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.viewsByNonFollowersHelp'),
        sortField: InstagramReelPostSortField.VIEWS_BY_NON_FOLLOWERS_RATE
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.viewsByNonFollowersRate, false)} unit="%" />
      )
    },
    {
      accessorKey: 'reach',
      header: t<string>('HeaderColumn.Reach'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.ReachPostHelp'),
        sortField: InstagramReelPostSortField.REACH
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.reach)} />
    },
    {
      accessorKey: 'videoViewTotalTime',
      header: t<string>('HeaderColumn.Watch Time'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.WatchTimeHelp'),
        sortField: InstagramReelPostSortField.VIDEO_VIEW_TOTAL_TIME
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatDurationToDaysHoursMinutesSeconds(row.original.videoViewTotalTime, t)} />
      )
    },
    {
      accessorKey: 'avgWatchTime',
      header: t<string>('HeaderColumn.Avg Watch Time'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.AvgWatchTimeHelp'),
        sortField: InstagramReelPostSortField.AVG_WATCH_TIME
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatDurationToDaysHoursMinutesSeconds(row.original.avgWatchTime, t)} />
      )
    },
    {
      accessorKey: 'interactions',
      header: t<string>('HeaderColumn.Interaction'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.interactionHelp'),
        sortField: InstagramReelPostSortField.INTERACTIONS
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.interactions)} />
    },
    {
      accessorKey: 'likes',
      header: t<string>('HeaderColumn.Like'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.LikeHelp'),
        sortField: InstagramReelPostSortField.LIKES
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.likes)} />
    },
    {
      accessorKey: 'comments',
      header: t<string>('HeaderColumn.Comment'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.CommentHelp'),
        sortField: InstagramReelPostSortField.COMMENT
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.comments)} />
    },
    {
      accessorKey: 'shares',
      header: t<string>('HeaderColumn.share'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.shareHelp'),
        sortField: InstagramReelPostSortField.SHARES
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.shares)} />
    },
    {
      accessorKey: 'saved',
      header: t<string>('HeaderColumn.Save'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.SaveHelp'),
        sortField: InstagramReelPostSortField.SAVED
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.saved)} />
    },
    {
      accessorKey: 'accountsEngaged',
      header: t<string>('HeaderColumn.Accounts Engaged'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.accountsEngagedHelp'),
        sortField: InstagramReelPostSortField.ACCOUNTS_ENGAGED
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.accountsEngaged)} />
    },
    {
      accessorKey: 'profileActivity',
      header: t<string>('HeaderColumn.Profile Activity'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.profileActivityHelp'),
        sortField: InstagramReelPostSortField.PROFILE_ACTIVITY
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.profileActivity)} />
    },
    {
      accessorKey: 'follows',
      header: t<string>('HeaderColumn.Follow'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.FollowHelp'),
        sortField: InstagramReelPostSortField.FOLLOWS
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.follows)} />
    },
    {
      accessorKey: 'postDate',
      header: t<string>('HeaderColumn.Post date'),
      meta: { help: t<string>('Tooltip.PostDateHelp'), sortField: InstagramReelPostSortField.POST_DATE },
      cell: ({ row }) => <TableCell.Date className="min-w-20" value={row.original.postDate} />
    }
  ];

  return { columns };
};
